import { createContext, useContext, useEffect, useState } from "react";
import primeClient from "../../axios/primeClient";
import { Card } from "../../Components/Card";
import {
  generateCircularProgressBarOption,
  PieChart,
} from "../../Components/PieChart";
import { useFetch } from "../../hooks/useFetch";
import { timeAgo } from "../../utils/timeAgo";
import { Circle } from "../../Components/Circle";
import Recommendations from "../../Components/Recommendations";
import { useTranslation } from "react-i18next";
import { Colors } from "../../constants/Colors";

const DataContext = createContext();

export const HiAlarmDetails = ({ alarmId }) => {
  const {
    data: alarmData,
    isLoading,
    error,
  } = useFetch({
    url: `/v1/apps/alarm/detail?alarm_id=${alarmId}`,
    client: primeClient,
  });

  const { t, i18n } = useTranslation();

  return (
    <DataContext.Provider value={{ alarmData, isLoading, error, t, i18n }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <DeviceDescriptionCard />
        <ChartsCard />
        <Recommendations
          rootCause={isLoading || error ? [] : [alarmData.alarm_status]}
        />
        <DetailAnalysisCard />
      </div>
    </DataContext.Provider>
  );
};

// prettier-ignore
const statusColors = {
  "online": Colors.alertsgreen100,
  "offline": Colors.alertsred100,
  "technical-issue": "#a7a9ac",
  "unknown": "#a7a9ac",
  "suspend": Colors.alertsyellow100,
}

// prettier-ignore
const levelColors = {
  "warning": "#ffb220",
  "critical": "#fb3640",
  "normal": "3ec300",
  "no_data": "#a7a9ac"
}

const DeviceDescriptionCard = () => {
  const { alarmData, isLoading, error, t } = useContext(DataContext);
  return (
    <Card
      style={{
        width: "100%",
        maxWidth: "400px",
        backgroundColor: Colors.primary100,
        color: Colors.white,
        padding: "0.5rem 1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
        }}
      >
        <div>{isLoading || error ? "Device Name" : alarmData.data.name}</div>
        {!isLoading && !error && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Circle
              color={statusColors[alarmData.alarm_status]}
              diameter="1rem"
            />
            {alarmData.alarm_status.toUpperCase()}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>{isLoading || error ? "xxx.xxx.xxx.xxx" : alarmData.nd}</div>
        <div>{!isLoading && !error && timeAgo(alarmData.updated_at, t)}</div>
      </div>
    </Card>
  );
};

const ChartsCard = () => {
  const { alarmData, isLoading, error, t, i18n } = useContext(DataContext);

  return (
    <Card
      style={{
        padding: "0.5rem 1rem",
        height: "fit-content",
        display: "flex",
        gap: "2rem",
        width: "650px",
      }}
    >
      <div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          {t("hi.deviceDetail.availability")}
        </div>
        {!isLoading && !error && alarmData && (
          <div
            style={{
              display: "flex",
              width: "180px",
              justifyContent: "center",
            }}
          >
            <PieChart
              options={generateCircularProgressBarOption(
                alarmData.data.availability + "%",
                null,
                "red",
                alarmData.data.availability
              )}
              chartWidth={"80px"}
              chartHeight={"80px"}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

const DetailAnalysisCard = () => {
  const { alarmData, isLoading, error, t } = useContext(DataContext);
  return (
    <Card style={{ padding: "0.5rem 1rem" }}>
      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
        {t("hi.deviceDetail.detailAnalysis")}
      </div>
      <div
        style={{
          padding: "0.5rem 1.5rem",
          borderRadius: "5px",
          height: "70%",
          display: "flex",
          justifyContent: "start",
          backgroundColor: Colors.white,
        }}
      >
        <div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.deviceName")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.name}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.status")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.alarm_status.toUpperCase()}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.location")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.lokasi}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.sto")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.sto}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.deviceMerk")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.device}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.attenuation")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.redaman}
            </span>
          </div>
          <div style={{ fontWeight: "bold" }}>
            {t("hi.deviceDetail.measurementDate")}:{" "}
            <span style={{ fontWeight: "normal" }}>
              {!isLoading && !error && alarmData.data.tgl_ukur}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};
